import React, { useEffect, useContext, useState, useMemo } from 'react';
import {
    TicketControls,
    TicketStats,
    TicketStatus,
    useProjectContext,
    Popup,
    Button,
    ViewContext,
    TicketDetails,
    TicketForm,
    Skeleton,
    EmptyState,
} from 'components/lib';
import Style from './tickets.tailwind';
import PannelStyle from 'components/pannel/pannel.tailwind';
import { TicketListView } from './listView';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { _t } from 'app/translations';
import { useLiveQuery } from 'components/hooks/websocket';
import { getUserCache } from 'utilities/cache';

const Tickets = () => {
    const {
        status: projectsStatus,
        selectedProject,
        isAllProjectSelected,
        projects,
        projectUsers,
    } = useProjectContext();
    let [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const context = useContext(ViewContext);
    const [isRendered, setIsRendered] = useState(false);

    const ticketId = searchParams.get('id');
    const categoryId = searchParams.get('c');
    const sortBy = searchParams.get('by');
    const order = searchParams.get('order');
    const priority = searchParams.get('priority');
    const assignee = searchParams.get('assignee');
    const projectName = searchParams.get('p');
    const onlyMe = searchParams.get('me');
    const creator = searchParams.get('creator');
    const query = searchParams.get('q');
    const viewMode = searchParams.get('view') === 'list' ? 'list' : 'kanban';

    const hasProjects = projectName || projects?.length;
    const { user_id } = getUserCache();

    function updateSearchParams(key, params) {
        if (searchParams.get(key) === params) return;
        let newParams = getSearchParams();

        newParams[key] = params;
        newParams = {
            ...searchParams,
            ...newParams,
        };

        searchParams.set(key, params);
        if (newParams.p === 'all') {
            delete newParams.c;
            searchParams.delete('c');
        }

        setSearchParams(searchParams);
    }

    const getSearchParams = () => {
        let params = {};
        searchParams.forEach((value, key) => {
            params[key] = value;
        });
        return params;
    };

    const setViewMode = (mode) => {
        const next = getSearchParams();
        if (mode === 'kanban') {
            delete next.view;
        } else {
            next.view = mode;
        }
        setSearchParams(next);
    };

    const ticketCategories = useLiveQuery(
        'ticket_categories',
        {
            project_id: selectedProject?.id,
        },
        {
            enabled: projectsStatus === 'success',
        },
    );

    // Category filter: single id, comma-separated multi, or "all".
    const categoryIds =
        !categoryId || categoryId === 'all' ? [] : categoryId.split(',').filter(Boolean);
    const singleCategoryId = categoryIds.length === 1 ? categoryIds[0] : undefined;

    const currCategoryData = ticketCategories?.data?.find((item) => {
        if (!categoryId || categoryId === 'all' || categoryIds.length !== 1) return true;
        return item.id === categoryIds[0];
    });

    // When multiple categories are selected, columns come from the first
    // selected category's template (or the first available) — the board still
    // needs a template, and mixing templates mid-board is undefined.
    const statusTemplateSource =
        categoryIds.length === 1
            ? ticketCategories?.data?.find((c) => c.id === categoryIds[0])
            : ticketCategories?.data?.[0];
    const statusTemplate =
        (categoryIds.length <= 1 ? currCategoryData : statusTemplateSource)?.status_template ||
        statusTemplateSource?.status_template;

    const creatorId = projectUsers?.find((user) => user.email === creator)?.user_id;

    const allTickets = useLiveQuery(
        'tickets',
        {
            by: sortBy,
            order: order,
            priority: priority,
            assignee: assignee,
            project_id: isAllProjectSelected ? undefined : selectedProject?.id,
            q: query,
            user_id: onlyMe ? user_id : creatorId ? creatorId : undefined,
        },
        {
            enabled: projectsStatus === 'success',
        },
    );

    const tickets = useLiveQuery(
        'tickets',
        {
            ticket_category_id: isAllProjectSelected ? undefined : singleCategoryId,
            by: sortBy,
            order: order,
            priority: priority,
            assignee: assignee,
            project_id: isAllProjectSelected ? undefined : selectedProject?.id,
            q: query,
            user_id: onlyMe ? user_id : creatorId ? creatorId : undefined,
        },
        {
            enabled: projectsStatus === 'success',
        },
    );

    // Multi-category: filter client-side when the API only accepts one id.
    // Memoised on the id list rather than the array so the derived value keeps
    // its identity across renders — TicketStats memoises on it, and rebuilding
    // it every render made every stat recompute every render.
    const categoryKey = categoryIds.join(',');
    const ticketsData = useMemo(() => {
        const data = tickets?.data || [];
        const ids = categoryKey ? categoryKey.split(',') : [];
        if (ids.length <= 1) return data;
        return data.filter((t) => ids.includes(t.ticket_category_id));
    }, [tickets?.data, categoryKey]);

    const currTicketData = ticketsData?.find((item) => {
        return item.number === ticketId;
    });

    const hasActiveFilters = !!(
        query ||
        priority ||
        assignee ||
        creator ||
        onlyMe ||
        (categoryId && categoryId !== 'all')
    );

    const isLoading =
        tickets?.status === 'loading' ||
        tickets?.isLoading ||
        ticketCategories?.status === 'loading';

    useEffect(() => {
        const currProjectName = selectedProject?.name.toLowerCase() || 'all';
        updateSearchParams('p', currProjectName);
    }, [selectedProject, ticketId]);

    useEffect(() => {
        if (!isRendered) return;
        if (!ticketId) {
            if (!searchParams.get('p')) {
                const currProjectName = selectedProject?.name.toLowerCase() || 'all';
                updateSearchParams('p', currProjectName);
            }
            if (!categoryId && ticketCategories?.data?.length) {
                updateSearchParams('c', 'all');
            }
        }
    }, [categoryId, projectName, ticketId, isRendered]);

    useEffect(() => {
        if (
            ticketCategories?.data?.length &&
            ticketCategories.status === 'success' &&
            projectName !== 'all'
        ) {
            if (!currTicketData && !ticketId) {
                if (!categoryId) {
                    updateSearchParams('c', 'all');
                }
            } else if (currTicketData) {
                updateSearchParams('c', currTicketData.ticket_category_id);
            }
        }
        setIsRendered(true);
    }, [ticketCategories.data, projectName, currTicketData]);

    useEffect(() => {
        if (projectName !== 'all' && ticketCategories?.data?.length) {
            if (!ticketId && !categoryId) {
                updateSearchParams('c', 'all');
            }
        }
    }, [selectedProject, ticketCategories?.data]);

    useEffect(() => {
        tickets.refetch();
        if (!ticketId && categoryId) {
            updateSearchParams('c', categoryId);
        }
    }, [categoryId]);

    useEffect(() => {
        if (projectName === 'all') {
            updateSearchParams('p', selectedProject?.name.toLowerCase() || 'all');
        }
    }, [projectName]);

    useEffect(() => {
        if (ticketId && projectsStatus === 'success') {
            context.pannel.show(
                {
                    hideClose: true,
                    className: PannelStyle.wide,
                    component: (
                        <TicketDetails
                            selectedTicket={ticketId}
                            category={categoryId}
                            selectedProject={selectedProject}
                        />
                    ),
                },
                () => {
                    searchParams.delete('id');
                    setSearchParams(searchParams);
                },
            );
        } else {
            context.pannel.hide();
        }
    }, [ticketId, projectName]);

    function sortTicketByStatus(status) {
        if (!ticketsData.length) return [];

        return ticketsData.filter((ticket) => {
            const {
                ticket_category: { status_template },
                integration_data: { status: ticketStatus },
            } = ticket;

            const categoryStatus = status_template.find((item) => {
                const { statuses } = item;
                return statuses.find((statusItem) => statusItem.id === ticketStatus);
            });

            return categoryStatus?.key === status.key;
        });
    }

    function openCreate() {
        context.modal.show({
            title: _t('ticket.create.title'),
            width: '48rem',
            component: (
                <TicketForm
                    cancel
                    categories={ticketCategories.data}
                    category={categoryId}
                />
            ),
        });
    }

    const visibleColumns = (statusTemplate || []).filter((item) => !item.hidden);

    const boardContent = (() => {
        if (isLoading) {
            return (
                <div className={Style.boardLoading}>
                    {(visibleColumns.length ? visibleColumns : Array.from({ length: 4 })).map(
                        (col, i) => (
                            <div
                                key={col?.key || i}
                                className="min-w-[280px] flex-1 space-y-3">
                                <Skeleton className="h-[52px] w-full rounded-lg" />
                                {Array.from({ length: 3 }).map((_, j) => (
                                    <Skeleton key={j} className="h-28 w-full rounded-lg" />
                                ))}
                            </div>
                        ),
                    )}
                </div>
            );
        }

        if (!ticketsData.length) {
            return (
                <EmptyState
                    className="min-h-[240px] flex-1 border-none bg-transparent"
                    bare
                    title={
                        hasActiveFilters
                            ? _t('ticket.noTicketsFiltered')
                            : _t('ticket.noTickets')
                    }
                    description={
                        hasActiveFilters
                            ? _t('ticket.noTicketsFilteredDescription')
                            : _t('ticket.noTicketsDescription')
                    }
                    buttonText={selectedProject ? _t('ticket.create.title') : undefined}
                    action={selectedProject ? openCreate : undefined}
                />
            );
        }

        return (
            <div className="h-full min-h-0">
                <div className={Style.board}>
                    {visibleColumns.map((item, index) => (
                        <TicketStatus
                            item={item}
                            tickets={sortTicketByStatus(item)}
                            key={item.key || index}
                            label={item.label}
                            color={item.color}
                            categoryParams={categoryId}
                        />
                    ))}
                </div>
            </div>
        );
    })();

    return (
        <div className={Style.page}>
            <TicketStats tickets={{ ...tickets, data: ticketsData }} />
            <div className={Style.stack}>
                <TicketControls
                    setSearchParams={setSearchParams}
                    searchParams={searchParams}
                    categoryParams={categoryId}
                    getSearchParams={getSearchParams}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    allTickets={allTickets?.data || []}
                />
                <div className={Style.boardWrap}>
                    {viewMode === 'list' ? (
                        <TicketListView
                            tickets={{ ...tickets, data: ticketsData }}
                            filtered={hasActiveFilters}
                            onCreate={openCreate}
                        />
                    ) : (
                        boardContent
                    )}
                </div>
            </div>

            <Popup
                show={!hasProjects && projectsStatus === 'success'}
                title={_t('error.noProject.title')}
                text={_t('error.noProject.text')}>
                <div className="mt-8">
                    <Button
                        text={_t('project.goto.multiple')}
                        color="blue"
                        action={() => navigate('/app/settings/projects')}
                    />
                </div>
            </Popup>
        </div>
    );
};

export default Tickets;
